import React, {useEffect, useState} from 'react';
import {logPageLoadEvents, sendEvarData} from '@ibibo/analytics';
import {format} from 'date-fns';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  LabelList,
  Tooltip,
  Line,
  Legend,
  Cross,
  Customized,
  Text,
} from 'recharts';
import GraphHeader from '../GraphHeader/GraphHeader';
import {monthsNoDataXaxis} from '../../../../../constants/dashboardConfig.js';

import * as S from './ProgressGraph.styles';
//import useWindowSize from '../useWindowSize';
import {formatNumber} from '../../../../../utils/Utilities';

function numFormatter(num, fixed = 1) {
  if (num > 999 && num <= 99999) {
    var re = new RegExp('^-?\\d+(?:.\\d{0,' + (fixed || -1) + '})?');
    num = num.toString().match(re)[0] / 1000;
    var with2Decimals = num.toString().match(/^-?\d+(?:\.\d{0,1})?/)[0];
    return with2Decimals + 'K';
  } else if (num >= 100000 && num < 10000000) {
    var re = new RegExp('^-?\\d+(?:.\\d{0,' + (fixed || -1) + '})?');
    num = num.toString().match(re)[0] / 100000;
    var with2Decimals = num.toString().match(/^-?\d+(?:\.\d{0,2})?/)[0];
    return with2Decimals + 'L';
  } else if (num < 999) {
    return Math.round(num * 100) / 100;
  }
}

const ProgressGraph = ({
  handleViewDetails,
  monthGmv,
  hotelTravelCompletedAmount,
  totalBookingAmount,
  graphData,
  internationalSpends,
  internationSpendsCallout,
  graphTemplateData,
}) => {
  const curMonth = new Date().getMonth();

  const [totalAmount, setTotalAmount] = useState(0);

  useEffect(() => {
    if (totalBookingAmount) {
      setTotalAmount(totalBookingAmount);
    }
  }, [totalBookingAmount]);

  const renderLabel = props => {
    const {x, y, value} = props;
    return (
      <text x={x + 15} y={296} fill={'#979797'} fontSize={'12px'} rotate="0" textAnchor="middle">
        {value ? 'Current Month' : ''}
      </text>
    );
  };
  const renderIcon = props => {
    const {x, y, value} = props;

    return value ? (
      <svg width={18} height={18} x={x - 2} y={y - 20}>
        <image width={18} height={18} href={value} />
      </svg>
    ) : null;
  };

  const CustomCursor = props => {
    const {x, y, width, height, stroke} = props;
    return <rect fill="#d3d3d359" x={x} y={y} width={width} height={height} />;
  };

  const CustomTooltip = ({active, payload, label}) => {
    useEffect(() => {
      if (active) {
        const events = {
          prop54: 'hero_dashboard_hover',
        };
        logPageLoadEvents('myPartner_loyaltyHero|landingPage', events);
      }
    }, [label]);
    if (active && payload && payload.length) {
      return (
        <S.CustomTooltip>
          <div className="makeFlex hrtlCenter appendBottom18">
            {payload[0].payload.heroIcon && <S.TooltipIcon src={payload[0].payload.heroIcon} />}
            <p className="latoBlack font14 blackText">
              Summary for {label} {payload[0].payload.year}{' '}
            </p>
          </div>
          {!(payload[0].value === 0 && payload[1].value === 0) && (
            <div className="paddingLR6 makeFlex hrtlCenter spaceBetween">
              <div className="makeFlex hrtlCenter appendRight40">
                <span className="bulletBoxTC" />
                <div>
                  <p className="label">Travel Completed</p>
                  <p className="font10">
                    <span className="latoBlack font12 blackText appendRight4">
                      ₹ {numFormatter(payload[0].value)}
                    </span>{' '}
                    {payload[0].payload.bookingTc}
                  </p>
                </div>
              </div>
              <div className="makeFlex hrtlCenter">
                <span className="bulletBoxY2T" />
                <div>
                  <p className="label">Yet to Travel</p>
                  <p className="font10">
                    <span className="latoBlack font12 blackText appendRight4">
                      ₹ {numFormatter(payload[1].value)}
                    </span>{' '}
                    {payload[1].payload.bookingY2c}
                  </p>
                </div>
              </div>
            </div>
          )}

          {!(
            (payload[0].value === 0 && payload[1].value === 0) ||
            (payload[2].value === 0 && payload[3].value === 0)
          ) && <S.Divider />}

          {!(payload[2].value === 0 && payload[3].value === 0) && (
            <p className="latoBold font11 blackText appendBottom6 appendLeft6">Hotel Bookings</p>
          )}

          {!(payload[2].value === 0 && payload[3].value === 0) && (
            <div className="paddingLR6 makeFlex hrtlCenter spaceBetween">
              <div className="makeFlex hrtlCenter appendRight40">
                <span className="bulletBoxHTC" />
                <div>
                  <p className="font10">
                    <span className="latoBlack font12 blackText appendRight4">
                      ₹ {numFormatter(payload[2].value)}
                    </span>{' '}
                    {payload[2].payload.bookingHtc}
                  </p>
                  <p className="label">Travel Completed</p>
                </div>
              </div>
              <div className="makeFlex hrtlCenter">
                <span className="bulletBoxY2T" />
                <div>
                  <p className="font10">
                    <span className="latoBlack font12 blackText appendRight4">
                      ₹ {numFormatter(payload[3].value)}
                    </span>{' '}
                    {payload[3].payload.bookingHy2c}
                  </p>
                  <p className="label">Yet to Travel</p>
                </div>
              </div>
            </div>
          )}
        </S.CustomTooltip>
      );
    }

    return null;
  };

  const winSize = window.screen.width;

  return (
    <S.PrgsGraph>
      <GraphHeader
        graphTemplateData={graphTemplateData?.heading}
        monthGmv={numFormatter(monthGmv)}
        hotelTravelCompletedAmount={numFormatter(hotelTravelCompletedAmount)}
        internationalSpends={numFormatter(internationalSpends)}
        internationalSpendsCallout={internationSpendsCallout}
      />
      <div style={{height: 300}}>
        <S.PrgsLegends>
          <li>Travel Completed</li>
          <li>Travel Pending</li>
          <li>Hero Zone</li>
          <li>Hotel Travel Completed</li>
        </S.PrgsLegends>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            width={500}
            height={300}
            data={
              graphData?.length
                ? graphData
                : curMonth >= 3 && curMonth <= 8
                ? monthsNoDataXaxis.dashboardAprilToSeptember
                : monthsNoDataXaxis.dashboardOctoberToMarch
            }
            margin={{
              top: 20,
              right: winSize > 767 ? 30 : 16,
              left: winSize > 767 ? 20 : 0,
              bottom: 5,
            }}
            barSize={15}
            barGap={0}
          >
            <CartesianGrid strokeDasharray="3 0" vertical={false} />
            <XAxis dataKey="name" />
            <YAxis
              interval={0}
              tickCount={8}
              ticks={[700000, 1500000, 2500000, 5000000, 10000000]}
              tick={e => {
                const {
                  payload: {value},
                } = e;
                const color = value === (600000 || 1200000 || 1800000) ? 'black' : 'black';
                e['fill'] = color;
                return (
                  <Text
                    style={{
                      fontWeight:
                        value === 600000
                          ? '800'
                          : value === 1200000
                          ? '800'
                          : value === 1800000
                          ? '800'
                          : '400',
                    }}
                    {...e}
                  >
                    {formatNumber(value)}
                  </Text>
                );
              }}
              tickFormatter={formatNumber}
              domain={['0, 10000000']}
            />
            {/* <Legend /> */}
            <Tooltip cursor={<CustomCursor />} content={<CustomTooltip />} />
            <Bar dataKey="travelCompleted" stackId="a" fill="#8D7DB0" />
            <Bar dataKey="travelPendingOne" stackId="a" fill="#C7B9E8">
              <LabelList dataKey="heroIcon" position="top" angle="90" content={renderIcon} />
              <LabelList
                dataKey="currentMonth"
                position="bottom"
                angle="90"
                content={renderLabel}
              />
            </Bar>
            <Bar dataKey="hotelTravelCompleted" stackId="b" fill="#504171" />
            <Bar dataKey="travelPendingTwo" stackId="b" fill="#C7B9E8" />
          </BarChart>
        </ResponsiveContainer>
      </div>
      {winSize <= 767 && (
        <S.PrgsJourneyCta onClick={handleViewDetails}>Your Hero Journey Summary </S.PrgsJourneyCta>
      )}
    </S.PrgsGraph>
  );
};

export default ProgressGraph;
